import React, { useEffect, useRef } from 'react'
import { Sidebar } from './Navigation/Sidebar'
import { Topbar } from './Navigation/Topbar'
import { BottomTabBar } from './Navigation/BottomTabBar'
import { MoreMenuSheet } from './Navigation/MoreMenuSheet'
import { MobileMenu } from './Navigation/MobileMenu'
import { ToastContainer } from './ui/Toast'
import { Softphone } from './Phone/Softphone'
import { ActiveCallPill } from './Phone/ActiveCallPill'
// AC-36: top-of-app soft-cap banner when tenant usage >= 80%.
import { UsageCapBanner } from './Billing/UsageCapBanner'
import { NotificationBanner } from '@/components/Phone/NotificationBanner'
import { useInboxStats } from '@/hooks/useApi'
import { useUIStore } from '@/stores/uiStore'

function InboxBadgeSync() {
  const { data } = useInboxStats()
  const setInboxBadge = useUIStore((s) => s.setInboxBadge)
  useEffect(() => {
    if (data?.unreadCount != null) setInboxBadge(data.unreadCount)
  }, [data?.unreadCount, setInboxBadge])
  return null
}

interface LayoutProps {
  children: React.ReactNode
}

/**
 * Publish the height of the in-flow banner stack as `--page-banner-h`.
 *
 * Pages that size a pane off the viewport (Inbox's message list, the calendar
 * grid) subtract a hard-coded chrome constant from 100dvh. Those constants
 * predate the banners, so every banner that appears pushes the pane that many
 * pixels past the bottom of the screen and the whole page starts scrolling.
 * Measuring here means the constant only has to describe fixed chrome, and
 * anything conditional is accounted for by the variable.
 */
function BannerStack({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const publish = () => {
      document.documentElement.style.setProperty('--page-banner-h', `${el.offsetHeight}px`)
    }
    publish()
    // Banners mount, unmount, and reflow on rotate/resize — a one-shot
    // measurement would go stale the first time one of them appeared.
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.removeProperty('--page-banner-h')
    }
  }, [])

  // `display: flow-root` is load-bearing, not styling. Without it the wrapper
  // has no block formatting context, so a child's top margin collapses
  // *through* it: `UsageCapBanner` is `mt-3`, and those 12px escaped
  // `offsetHeight` entirely. The variable then under-reported the banner by
  // 12px and the pane it feeds stayed 12px too tall — the same overflow, just
  // smaller.
  return <div ref={ref} style={{ display: 'flow-root' }}>{children}</div>
}

export function Layout({ children }: LayoutProps) {
  // Sidebar width is consumed by descendants via the --sidebar-w CSS var
  // (e.g. desktop kanban ultrawide break-out, so it doesn't slide under
  // the sidebar). Sidebar widths are w-16 (64px) collapsed and w-64
  // (256px) expanded. Mobile (<768px) hides the sidebar entirely; the
  // ultrawide break-out that consumes this only fires at ≥1600px, so
  // we can safely assume desktop here.
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const sidebarWidth = sidebarCollapsed ? 64 : 256

  return (
    <div
      className="safe-area-layout relative flex h-screen-safe bg-black overflow-x-clip"
      style={{ ['--sidebar-w' as string]: `${sidebarWidth}px` }}
    >
      {/* MIDNIGHT: pure #000 canvas — aurora divs removed */}
      <InboxBadgeSync />

      {/* Sidebar — desktop only */}
      <Sidebar />

      {/* Main area */}
      <div className="relative z-10 flex flex-col flex-1 min-w-0">
        <div className="relative z-30 shrink-0">
          <Topbar />
        </div>
        {/* Page content — on mobile the Topbar is position:fixed, so add matching top padding.
            Reads --safe-top from index.css to avoid hand-rolling env() (CLAUDE.md §8). */}
        <main className="main-scroll-area flex-1 overflow-y-auto pt-[calc(3.5rem+var(--safe-top))] xl:pt-0">
          <BannerStack>
            {/* AC-36: soft-cap banner — renders only when >= 80% */}
            <UsageCapBanner />
            {/* In flow, alongside UsageCapBanner. Previously a fixed overlay
                inside the softphone's modal group, where it covered the page
                header on mobile. */}
            <NotificationBanner />
          </BannerStack>
          <div className="animate-fade-up">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile side drawer — triggered by hamburger in Topbar */}
      <MobileMenu />

      {/* iOS Bottom Tab Bar — mobile only */}
      <BottomTabBar />
      <MoreMenuSheet />

      {/* Toast notifications */}
      <ToastContainer />

      {/* Softphone widget — always visible (mounted outside route tree
          so the Twilio Device + active call survive navigation). */}
      <Softphone />

      {/* iOS-style "tap to return to call" pill — visible on every route
          while a call is in progress and the softphone is collapsed. */}
      <ActiveCallPill />
    </div>
  )
}
